/**
 * @fileoverview Repository configuration utilities
 */

import type { RepositoryConfig } from '../../types/common/PocketBaseTypes';

/**
 * Default configuration for repositories
 */
export const DEFAULT_REPOSITORY_CONFIG: RepositoryConfig = {
  defaultSort: '-created',
  defaultFilter: '',
  defaultPageSize: 10,
  enableSoftDelete: false
};

/**
 * Configuration for collections that support soft delete
 */
export const SOFT_DELETE_CONFIG: RepositoryConfig = {
  ...DEFAULT_REPOSITORY_CONFIG,
  defaultFilter: 'activo=true',
  enableSoftDelete: true
};

/**
 * Predefined configurations for common collection patterns
 */
export const REPOSITORY_CONFIGS = {
  /** For simple collections without soft delete */
  SIMPLE: DEFAULT_REPOSITORY_CONFIG,
  
  /** For collections with soft delete (activo field) */
  SOFT_DELETE: SOFT_DELETE_CONFIG,
  
  /** For lookup/catalog collections */
  CATALOG: {
    ...SOFT_DELETE_CONFIG,
    defaultSort: 'nombre',
    defaultPageSize: 50
  },
  
  /** For parameter/configuration collections */
  PARAMETERS: {
    ...SOFT_DELETE_CONFIG,
    defaultSort: 'nombre',
    defaultFilter: 'estado=true'
  },
  
  /** For audit/log collections */
  AUDIT: {
    ...DEFAULT_REPOSITORY_CONFIG,
    defaultSort: '-created',
    defaultPageSize: 25,
    enableSoftDelete: false
  }
} as const;

/**
 * Configuration builder for custom repository setups
 */
export class RepositoryConfigBuilder {
  private config: RepositoryConfig;

  constructor(baseConfig: RepositoryConfig = DEFAULT_REPOSITORY_CONFIG) {
    this.config = { ...baseConfig };
  }

  /**
   * Set default sort order
   */
  withSort(sort: string): RepositoryConfigBuilder {
    this.config.defaultSort = sort;
    return this;
  }

  /**
   * Set default filter
   */
  withFilter(filter: string): RepositoryConfigBuilder {
    this.config.defaultFilter = filter;
    return this;
  }

  /**
   * Set default page size
   */
  withPageSize(pageSize: number): RepositoryConfigBuilder {
    this.config.defaultPageSize = pageSize;
    return this;
  }

  /**
   * Enable soft delete functionality
   */
  withSoftDelete(enabled: boolean = true): RepositoryConfigBuilder {
    this.config.enableSoftDelete = enabled;
    if (enabled && !this.config.defaultFilter.includes('activo=true')) {
      this.config.defaultFilter = this.config.defaultFilter 
        ? `${this.config.defaultFilter} && activo=true`
        : 'activo=true';
    }
    return this;
  }

  /**
   * Build the final configuration
   */
  build(): RepositoryConfig {
    return { ...this.config };
  }
}

/**
 * Helper function to create repository configuration
 */
export function createRepositoryConfig(
  options: Partial<RepositoryConfig> = {}
): RepositoryConfig {
  return {
    ...DEFAULT_REPOSITORY_CONFIG,
    ...options
  };
}

/**
 * Helper function to merge query options with repository defaults
 */
export function mergeQueryOptions(
  config: RepositoryConfig,
  options: any = {}
): any {
  const merged: any = { ...options };

  // Apply default sort if not provided
  if (!merged.sort && config.defaultSort) {
    merged.sort = config.defaultSort;
  }

  // Apply default filter if not provided
  if (!merged.filter && config.defaultFilter) {
    merged.filter = config.defaultFilter;
  } else if (merged.filter && config.defaultFilter) {
    // Combine filters with AND
    merged.filter = `(${config.defaultFilter}) && (${merged.filter})`;
  }

  return merged;
}
